import { useDashboardAudience } from './dashboardAudience'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate } from 'react-router-dom'
import { Download } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { EmptyState } from '@/components/ui/empty-state'
import { EventListHeader } from '@/components/events/engagement-event-controls'
import { EngagementEventTable } from '@/components/events/engagement-event-table'
import { ApiError, getJSON } from '@/lib/api'
import type {
  PortalEventRegistrations,
  PortalRegistrationEvent,
} from './portalEventRegistrations.types'
import { usePortalEventListState } from './usePortalEventListState'
import { usePortalActivityDownload } from './usePortalActivityDownload'
import { PortalEventPeopleSheet } from './PortalEventPeopleSheet'

export function MemberPortalEventRegistrations({
  organizationId,
  viewer,
}: {
  organizationId: number
  viewer: string
}) {
  const audience = useDashboardAudience(organizationId)
  const state = usePortalEventListState(organizationId)
  const [opened, setOpened] = useState<{
    row: PortalRegistrationEvent
    trigger: HTMLElement
  } | null>(null)
  const query = useQuery({
    queryKey: [
      audience.preview ? 'organization-dashboard-preview' : 'member-portal-dashboard',
      viewer,
      organizationId,
      'event_registrations',
      state.path,
    ],
    queryFn: () => getJSON<{ event_registrations: PortalEventRegistrations }>(state.path),
    retry: false,
  })
  const csv = usePortalActivityDownload(state.csvPath, 'organization-event-registrations.csv')
  const data = query.isError ? undefined : query.data?.event_registrations
  if (
    !audience.preview &&
    (csv.forbidden || (query.error instanceof ApiError && query.error.status === 404))
  )
    return <Navigate to="/portal" replace />
  return (
    <>
      <Card className="min-w-0 overflow-hidden">
        <EventListHeader
          data={data}
          params={state.params}
          update={state.update}
          csvAction={
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5"
              disabled={!data || csv.pending}
              onClick={() => void csv.download()}
            >
              <Download aria-hidden="true" className="size-3.5" />
              CSV
            </Button>
          }
        />
        {csv.error && <Alert variant="destructive">CSV unavailable. Try downloading again.</Alert>}
        {query.isPending && <EmptyState variant="quiet" title="Loading event registrations" />}
        {query.isError && (
          <Alert variant="destructive">
            Event registrations unavailable.{' '}
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </Alert>
        )}
        {data && (
          <EventResults
            data={data}
            state={state}
            onOpen={(row, trigger) => setOpened({ row, trigger })}
          />
        )}
      </Card>
      {opened && (
        <PortalEventPeopleSheet
          key={opened.row.event.id}
          organizationId={organizationId}
          viewer={viewer}
          {...opened}
          onClose={() => setOpened(null)}
        />
      )}
    </>
  )
}

function EventResults({
  data,
  state,
  onOpen,
}: {
  data: PortalEventRegistrations
  state: ReturnType<typeof usePortalEventListState>
  onOpen: (row: PortalRegistrationEvent, trigger: HTMLElement) => void
}) {
  const audience = useDashboardAudience(0)
  return (
    <>
      {data.rows.length ? (
        <EngagementEventTable
          rows={data.rows}
          sort={state.params.get('sort')!}
          onSort={(sort) => state.update('sort', sort)}
          onOpen={onOpen}
          eventHref={(row) =>
            !audience.preview && row.event.portal_visible === true && /^\d+$/.test(row.event.id)
              ? `/events/${row.event.id}`
              : undefined
          }
          sortableColumns={['date', 'places']}
          initialSortDirection="desc"
          eventCaption={(row) => <EventInvoice row={row} />}
        />
      ) : (
        <EmptyState
          variant="quiet"
          title={data.empty_message ?? 'No event registrations'}
          action={
            data.has_registrations ? (
              <Button variant="outline" onClick={state.clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      )}
      <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
        <span className="text-caption text-muted-foreground">{data.pagination.caption}</span>
        <div className="flex gap-2">
          {(['previous', 'next'] as const).map((direction) => {
            const cursor = data.pagination[`${direction}_cursor`]
            return (
              cursor !== null && (
                <Button
                  key={direction}
                  variant="outline"
                  size="sm"
                  onClick={() => state.update('cursor', cursor)}
                >
                  {direction === 'previous' ? 'Previous' : 'Next'}
                </Button>
              )
            )
          })}
        </div>
      </div>
    </>
  )
}

function EventInvoice({ row }: { row: PortalRegistrationEvent }) {
  if (!row.invoice) return null
  return (
    <Link
      to={`/portal/billing/invoices/${row.invoice.id}`}
      className="block text-caption text-primary hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      Invoice {row.invoice.label}
    </Link>
  )
}
